import { Component, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NgxI18nService } from '@studiohyperdrive/ngx-i18n';
import { validateContent } from '@studiohyperdrive/rxjs-utils';
import { switchMap, takeUntil, tap } from 'rxjs';

import { MetaService } from '@cjm/shared/core';
import { VLoketAppRoutePaths } from '@cjm/shared/route-paths';
import { OnDestroyComponent } from '@cjm/shared/types';
import { UserEntity, UserService } from '@cjm/shared/user';
import {
	AdviceDetailPageEntity,
	AssociationActivityEntity,
	IconListItem,
	MyAssociationEntity,
	VloketNewsletterComponent
} from '@cjm/v-loket/shared';

import { HomeFacade, InTheSpotlightEntity } from '../../../data';
import { I18nKeys } from '../../../i18n';
import {
	AboutUsSectionComponent,
	AssociationPassportSectionComponent,
	HomeHeaderComponent,
	HomeHeaderLink,
	HomeHeaderLinks,
	InTheSpotlightSectionComponent,
	WeGrowTogetherSectionComponent
} from '../../components';

@Component({
	selector: 'cjm-app-home',
	templateUrl: 'home.component.html',
	providers: [HomeFacade],
	imports: [
		HomeHeaderComponent,
		AssociationPassportSectionComponent,
		InTheSpotlightSectionComponent,
		WeGrowTogetherSectionComponent,
		AboutUsSectionComponent,
		VloketNewsletterComponent
	]
})
export class HomePageComponent extends OnDestroyComponent implements OnInit {
	public readonly i18nKeys: typeof I18nKeys = I18nKeys;

	public readonly myAssociationLoading: Signal<boolean> = toSignal(this.homeFacade.myAssociationLoading$);
	public readonly myAssociationError: Signal<boolean> = toSignal(this.homeFacade.myAssociationError$);
	public readonly myAssociationProgress: Signal<IconListItem[]> = toSignal(this.homeFacade.myAssociationProgress$);
	public readonly myAssociationProgressCompleted: Signal<boolean> = toSignal(
		this.homeFacade.myAssociationProgressCompleted$
	);

	// Spotlight: Recommended Advice Pages
	public readonly advicePages: Signal<AdviceDetailPageEntity[]> = toSignal(this.homeFacade.advicePages$);
	public readonly adviceLoading: Signal<boolean> = toSignal(this.homeFacade.advicePagesLoading$);
	// Spotlight: Associations & Products
	public readonly spotlightItems: Signal<InTheSpotlightEntity> = toSignal(this.homeFacade.spotlightItems$);
	public readonly spotlightItemsLoading: Signal<boolean> = toSignal(this.homeFacade.spotlightItemsLoading$);
	public readonly settingProductAsFavorite: Signal<string | number> = this.homeFacade.settingFavoriteInProgress;

	public readonly user: Signal<UserEntity> = toSignal(this.userService.user$);

	public readonly appRoutePaths: typeof VLoketAppRoutePaths = VLoketAppRoutePaths;

	public readonly primaryHeaderLinks: HomeHeaderLinks = {
		title: I18nKeys.PageTitles.VLoket.Offers,
		blockLinks: [
			{
				title: I18nKeys.Home.Header.Links.Offers.BlockLink,
				url: [this.appRoutePaths.Offers]
			}
		],
		links: [
			{
				title: I18nKeys.Home.Header.Links.Offers.Urls.AdviceAndGuidance,
				url: [this.appRoutePaths.Offers],
				queryParams: {
					filters: `{"${this.i18nService.getTranslation(
						I18nKeys.Home.Header.Links.Offers.QueryParam
					)}":["coachingenadvies"]}`
				}
			},
			{
				title: I18nKeys.Home.Header.Links.Offers.Urls.Evidence,
				url: [this.appRoutePaths.Offers],
				queryParams: {
					filters: `{"${this.i18nService.getTranslation(
						I18nKeys.Home.Header.Links.Offers.QueryParam
					)}":["bewijs"]}`
				}
			},
			{
				title: I18nKeys.Home.Header.Links.Offers.Urls.FinancialAdvantage,
				url: [this.appRoutePaths.Offers],
				queryParams: {
					filters: `{"${this.i18nService.getTranslation(
						I18nKeys.Home.Header.Links.Offers.QueryParam
					)}":["financieelvoordeel"]}`
				}
			},
			{
				title: I18nKeys.Home.Header.Links.Offers.Urls.FinancialObligation,
				url: [this.appRoutePaths.Offers],
				queryParams: {
					filters: `{"${this.i18nService.getTranslation(
						I18nKeys.Home.Header.Links.Offers.QueryParam
					)}":["financiele_verplichting"]}`
				}
			},
			{
				title: I18nKeys.Home.Header.Links.Offers.Urls.InfrastructureAndMaterial,
				url: [this.appRoutePaths.Offers],
				queryParams: {
					filters: `{"${this.i18nService.getTranslation(
						I18nKeys.Home.Header.Links.Offers.QueryParam
					)}":["infrastructuur_materiaal"]}`
				}
			},
			{
				title: I18nKeys.Home.Header.Links.Offers.Urls.Admission,
				url: [this.appRoutePaths.Offers],
				queryParams: {
					filters: `{"${this.i18nService.getTranslation(
						I18nKeys.Home.Header.Links.Offers.QueryParam
					)}":["toelating"]}`
				}
			}
		]
	};
	public readonly secondaryHeaderLinks: WritableSignal<HomeHeaderLinks> = signal(null);
	public readonly secondaryHeaderLinksLoading: WritableSignal<boolean> = signal(true);

	constructor(
		public readonly userService: UserService,
		private readonly homeFacade: HomeFacade,
		private readonly metaService: MetaService,
		private readonly router: Router,
		private readonly i18nService: NgxI18nService
	) {
		super();
	}

	public ngOnInit(): void {
		this.homeFacade.init().pipe(takeUntil(this.destroyed$)).subscribe();

		this.metaService.updateMetaData({
			title: this.i18nService.getTranslation(this.i18nKeys.Home.Header.Title.withoutName),
			description: this.i18nService.getTranslation(this.i18nKeys.Home.Header.Intro),
			pageUrl: this.router.url
		});

		this.homeFacade.myAssociation$
			.pipe(
				// Denis: if the value is `null`, the user is not a VER, so only check for undefined.
				validateContent({ customValidatorFn: (data: MyAssociationEntity) => typeof data !== 'undefined' }),
				tap((data: MyAssociationEntity) => {
					// Denis: Check if the association exists.
					const hasAssociation = !!data;
					// Denis: Check if the association has mainActivities.
					const hasActivities = Array.isArray(data?.mainActivities) && data.mainActivities.length > 0;
					// Denis: Check if the association is public.
					const isPublic = data?.metaData?.isPublic || false;
					const vCode = data?.associationNumber;
					const activities: HomeHeaderLink[] = this.parseActivities(
						hasAssociation,
						hasActivities,
						data?.mainActivities
					);

					this.secondaryHeaderLinks.set({
						title: I18nKeys.PageTitles.Associations,
						blockLinks: [
							{
								title: I18nKeys.Home.Header.Links.Associations.BlockLink,
								url: [this.appRoutePaths.Associations]
							},
							// Denis: If the user is an association, check if it is public,
							// if so, create a link to the public detail.
							...(isPublic
								? [
										{
											title: I18nKeys.Home.Header.Links.Associations.MyAssociationLink,
											url: [this.appRoutePaths.Associations, vCode]
										}
									]
								: [])
						],
						links: activities
					});

					this.secondaryHeaderLinksLoading.set(false);
				}),
				takeUntil(this.destroyed$)
			)
			.subscribe();
	}

	/**
	 * parseActivities
	 *
	 * This function is used to parse the activities of the association.
	 * It will return the activities of the association if it has any, otherwise it will return the default activities.
	 * The default activities are the following:
	 * - Buurtwerking & Wijkcomité - BWWC
	 * - Gezondheid & Welzijn - GEWE
	 * - Jeugdwerk - JGDW
	 * - Kunsten, Erfgoed & Cultuur - KECU
	 * - Onderwijs & Vorming - VORM
	 * - Sport - SPRT
	 *
	 * If the user is logged in as an association without activities, only the first 3 default activities will be returned.
	 *
	 * @param hasAssociation
	 * @param hasActivities
	 * @param activities
	 * @returns HomeHeaderLink[]
	 */
	public parseActivities(
		hasAssociation: boolean,
		hasActivities: boolean,
		activities: AssociationActivityEntity[] = []
	): HomeHeaderLink[] {
		if (hasAssociation && hasActivities) {
			return activities.map((activity: AssociationActivityEntity) => ({
				title: activity.name,
				url: [this.appRoutePaths.Associations],
				queryParams: {
					filters: `{"${this.i18nService.getTranslation(
						I18nKeys.Home.Header.Links.Associations.QueryParam
					)}":["${activity.id}"]}`
				}
			}));
		}

		const defaultActivities = [
			{
				// Buurtwerking & Wijkcomité - BWWC
				title: I18nKeys.Home.Header.Links.Associations.Urls.NeighborhoodOperationAndCommittee,
				url: [this.appRoutePaths.Associations],
				queryParams: {
					filters: `{"${this.i18nService.getTranslation(
						I18nKeys.Home.Header.Links.Associations.QueryParam
					)}":["BWWC"]}`
				}
			},
			{
				// Gezondheid & Welzijn - GEWE
				title: I18nKeys.Home.Header.Links.Associations.Urls.HealthAndWelfare,
				url: [this.appRoutePaths.Associations],
				queryParams: {
					filters: `{"${this.i18nService.getTranslation(
						I18nKeys.Home.Header.Links.Associations.QueryParam
					)}":["GEWE"]}`
				}
			},
			{
				// Jeugdwerk - JGDW
				title: I18nKeys.Home.Header.Links.Associations.Urls.YouthWork,
				url: [this.appRoutePaths.Associations],
				queryParams: {
					filters: `{"${this.i18nService.getTranslation(
						I18nKeys.Home.Header.Links.Associations.QueryParam
					)}":["JGDW"]}`
				}
			},
			{
				// Kunsten, Erfgoed & Cultuur - KECU
				title: I18nKeys.Home.Header.Links.Associations.Urls.ArtsHeritageAndCulture,
				url: [this.appRoutePaths.Associations],
				queryParams: {
					filters: `{"${this.i18nService.getTranslation(
						I18nKeys.Home.Header.Links.Associations.QueryParam
					)}":["KECU"]}`
				}
			},
			{
				// Onderwijs & Vorming - VORM
				title: I18nKeys.Home.Header.Links.Associations.Urls.Education,
				url: [this.appRoutePaths.Associations],
				queryParams: {
					filters: `{"${this.i18nService.getTranslation(
						I18nKeys.Home.Header.Links.Associations.QueryParam
					)}":["VORM"]}`
				}
			},
			{
				//Sport - SPRT
				title: I18nKeys.Home.Header.Links.Associations.Urls.Sport,
				url: [this.appRoutePaths.Associations],
				queryParams: {
					filters: `{"${this.i18nService.getTranslation(
						I18nKeys.Home.Header.Links.Associations.QueryParam
					)}":["SPRT"]}`
				}
			}
		];

		if (hasAssociation) {
			return defaultActivities.splice(0, 3);
		}

		return defaultActivities;
	}

	/**
	 * handleSetAsFavorite
	 *
	 * The handleSetAsFavorite method will set a item as primary.
	 *
	 * @param id
	 */
	public handleSetAsFavorite(id: string | number): void {
		if (!id) {
			return;
		}

		this.userService
			.checkAuthentication()
			.pipe(
				switchMap(({ isAssociation, targetCode }) => {
					if (isAssociation) {
						return this.homeFacade.setProductAsFavorite(id);
					} else {
						const url = `/${this.i18nService.currentLanguage}/${VLoketAppRoutePaths.Offers}/${VLoketAppRoutePaths.OffersDetail}/${id}?action=markAsFavorite`;
						this.homeFacade.showFavoriteLoginModal(targetCode, url);

						return [];
					}
				}),
				takeUntil(this.destroyed$)
			)
			.subscribe();
	}

	/**
	 * handleRemoveAsFavorite
	 *
	 * The handleRemoveAsFavorite method will remove a item as primary.
	 *
	 * @param id
	 */
	public handleRemoveAsFavorite(id: string | number): void {
		if (!id) {
			return;
		}

		this.userService
			.checkAuthentication()
			.pipe(
				switchMap(({ isAssociation, targetCode }) => {
					if (isAssociation) {
						return this.homeFacade.removeProductFromFavorites(id);
					} else {
						this.homeFacade.showFavoriteLoginModal(targetCode);

						return [];
					}
				}),
				takeUntil(this.destroyed$)
			)
			.subscribe();
	}
}
